// SPDX-License-Identifier: AGPL-3.0-or-later

import {PRODUCT_NAME} from '@app/features/app/config/ProductConstants';
import {resolveAppShellBranding} from '@app/features/app/state/AppShellBranding';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import Theme from '@app/features/theme/state/Theme';
import {
	type BrandSvgProps,
	getDataFlx,
	getImageSizingProps,
	getThemeWordmarkUrl,
} from '@app/features/ui/components/icons/BrandImageUtils';
import AceWordmark from '@app/media/images/ace-wordmark-adaptive.svg?react';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const APPLICATION_WORDMARK_DESCRIPTOR = msg({
	message: '{productName} wordmark',
	comment: 'Accessible label for the application wordmark.',
});

interface FluxerWordmarkProps extends BrandSvgProps {
	variant?: 'default' | 'monochrome';
}

export const FluxerWordmark = observer(({variant: _variant, ...props}: FluxerWordmarkProps) => {
	const {i18n} = useLingui();
	const branding = resolveAppShellBranding(RuntimeConfig.getSnapshotOrNull());
	const productName = branding.productName;
	const ariaLabel = i18n._(APPLICATION_WORDMARK_DESCRIPTOR, {productName});
	// Keep a height-constrained wordmark proportional even under global img rules.
	const wordmarkStyle: React.CSSProperties = {
		...(props.height !== undefined ? {height: props.height as number | string, width: props.width ?? 'auto'} : {}),
		...props.style,
	};
	if (branding.wordmarkUrl !== null) {
		return (
			<img
				{...getImageSizingProps(props)}
				style={wordmarkStyle}
				src={getThemeWordmarkUrl(branding.wordmarkUrl, Theme.effectiveTheme === 'light')}
				alt={ariaLabel}
				data-flx={getDataFlx(props, 'ui.icons.fluxer-wordmark.img')}
			/>
		);
	}
	if (productName === PRODUCT_NAME) {
		return (
			<AceWordmark
				data-flx="ui.icons.fluxer-wordmark.img"
				{...props}
				role="img"
				aria-label={ariaLabel}
				style={{color: 'var(--text-primary)', ...wordmarkStyle}}
			/>
		);
	}
	{
		const style: React.CSSProperties = {
			...(props.style as React.CSSProperties | undefined),
			alignItems: 'center',
			display: 'inline-flex',
			fontWeight: 800,
			lineHeight: 1,
		};
		return (
			<span
				className={props.className}
				style={style}
				role="img"
				aria-label={ariaLabel}
				data-flx={getDataFlx(props, 'ui.icons.fluxer-wordmark.text')}
			>
				{productName}
			</span>
		);
	}
});
